import React, { useCallback, useMemo } from 'react';
import { Code2, PenLine } from 'lucide-react';
import SlateEmailEditor, { type SlateEmailEditorRef } from '../../components/editor/SlateEmailEditor';
import { cn } from '../../lib/utils';

const EDITOR_MIN_H = 220;
const EDITOR_MAX_H = 640;
const RESIZE_GRIP_H = 12;
/** Matches SlateEmailToolbar minHeight (34) + vertical padding (p-1.5). */
const EDITOR_HEADER_H = 46;

function useResizeHandle(minH: number, maxH: number, initialH: number) {
  const [height, setHeight] = React.useState(initialH);

  const onResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const startY = e.clientY;
      const startH = height;

      const onMove = (moveEvent: MouseEvent) => {
        const dy = moveEvent.clientY - startY;
        setHeight(Math.min(maxH, Math.max(minH, startH + dy)));
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [height, minH, maxH],
  );

  return { height, onResizeMouseDown };
}

type ResizeGripProps = {
  onMouseDown: (e: React.MouseEvent) => void;
  colors: Record<string, string>;
};

function ResizeGrip({ onMouseDown, colors }: ResizeGripProps) {
  return (
    <div
      role="separator"
      aria-label="Resize editor"
      onMouseDown={onMouseDown}
      title="Drag to resize"
      className={cn(
        'relative z-10 w-full flex-shrink-0 cursor-n-resize select-none border-t flex items-center justify-center',
        colors.border,
        'bg-black/[0.03] dark:bg-white/[0.04] hover:bg-indigo-500/10 active:bg-indigo-500/15',
      )}
      style={{ minHeight: RESIZE_GRIP_H, height: RESIZE_GRIP_H }}
    >
      <span className={cn('w-10 h-1 rounded-full opacity-40', colors.border, 'bg-current')} />
    </div>
  );
}

type ModeToggleProps = {
  htmlMode: boolean;
  onChange: (htmlMode: boolean) => void;
  colors: Record<string, string>;
};

function ModeToggle({ htmlMode, onChange, colors }: ModeToggleProps) {
  return (
    <div
      role="tablist"
      aria-label="Editor mode"
      className={cn('inline-flex rounded-lg border p-0.5 gap-0.5', colors.border, colors.surfaceHover)}
    >
      <button
        type="button"
        role="tab"
        aria-selected={!htmlMode}
        onClick={() => onChange(false)}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-all',
          !htmlMode
            ? 'bg-indigo-600 text-white shadow-sm dark:bg-indigo-500'
            : cn(colors.textMuted, colors.interactiveHover),
        )}
      >
        <PenLine className="w-3.5 h-3.5" aria-hidden />
        Visual
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={htmlMode}
        onClick={() => onChange(true)}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-all',
          htmlMode
            ? 'bg-indigo-600 text-white shadow-sm dark:bg-indigo-500'
            : cn(colors.textMuted, colors.interactiveHover),
        )}
      >
        <Code2 className="w-3.5 h-3.5" aria-hidden />
        HTML
      </button>
    </div>
  );
}

export type SignatureBodyEditorProps = {
  body: string;
  onChange: (value: string) => void;
  htmlMode: boolean;
  onHtmlModeChange: (htmlMode: boolean) => void;
  contentKey: number | string;
  placeholder: string;
  colors: Record<string, string>;
  editorRef: React.RefObject<SlateEmailEditorRef | null>;
};

const SignatureBodyEditor: React.FC<SignatureBodyEditorProps> = ({
  body,
  onChange,
  htmlMode,
  onHtmlModeChange,
  contentKey,
  placeholder,
  colors,
  editorRef,
}) => {
  const { height, onResizeMouseDown } = useResizeHandle(EDITOR_MIN_H, EDITOR_MAX_H, 320);

  const lineCount = useMemo(() => {
    if (!body) return 0;
    return body.split('\n').length;
  }, [body]);

  const charCount = body.length;

  const layerBottom = RESIZE_GRIP_H;

  return (
    <div className="w-full max-w-full space-y-2">
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <p className={cn('text-sm font-medium', colors.text)}>Body</p>
          <ModeToggle htmlMode={htmlMode} onChange={onHtmlModeChange} colors={colors} />
        </div>
        <p className={cn('text-xs', colors.textMuted)}>
          {htmlMode
            ? 'Edit raw HTML for your signature.'
            : 'Use the toolbar to format text, links, and tables.'}
        </p>
      </div>

      <div
        className={cn(
          'relative w-full min-w-0 max-w-full rounded-xl border shadow-sm overflow-hidden flex flex-col',
          colors.border,
          colors.surface,
        )}
        style={{ height: `${height}px` }}
      >
        <div
          className={cn(
            'absolute inset-x-0 top-0 flex flex-col min-w-0 min-h-0 overflow-hidden',
            htmlMode && 'invisible pointer-events-none',
          )}
          style={{ bottom: layerBottom }}
          aria-hidden={htmlMode}
        >
          <SlateEmailEditor
            key={contentKey}
            ref={editorRef as React.RefObject<SlateEmailEditorRef>}
            value={body}
            onChange={onChange}
            placeholder={placeholder}
            contentKey={contentKey}
            coalescePlainTextBlocks={false}
            fillHeight
            colors={colors}
            className="border-0 rounded-none shadow-none flex-1 min-h-0 w-full min-w-0 max-w-full"
          />
        </div>

        <div
          className={cn(
            'absolute inset-x-0 top-0 flex flex-col min-w-0 min-h-0 overflow-hidden',
            !htmlMode && 'invisible pointer-events-none',
          )}
          style={{ bottom: layerBottom }}
          aria-hidden={!htmlMode}
        >
          <div
            className={cn(
              'flex shrink-0 items-center justify-end gap-2 border-b px-3',
              colors.border,
              colors.surfaceHover,
            )}
            style={{ minHeight: EDITOR_HEADER_H, height: EDITOR_HEADER_H }}
          >
            <span className={cn('text-[11px] tabular-nums', colors.textMuted)}>
              {lineCount} {lineCount === 1 ? 'line' : 'lines'} · {charCount} chars
            </span>
          </div>
          <textarea
            value={body}
            onChange={(e) => onChange(e.target.value)}
            placeholder={'<p>Best regards,</p>\n<p><strong>Your Name</strong></p>\n<p>Company · phone · email</p>'}
            spellCheck={false}
            tabIndex={htmlMode ? 0 : -1}
            className={cn(
              'box-border flex-1 w-full min-w-0 max-w-full min-h-0 p-3 text-sm leading-relaxed font-mono resize-none',
              'focus:outline-none focus:ring-0 border-0',
              'bg-slate-50 dark:bg-slate-950/50',
              colors.text,
            )}
            style={{ fontSize: 14 }}
          />
        </div>

        <ResizeGrip onMouseDown={onResizeMouseDown} colors={colors} />
      </div>

      {htmlMode && (
        <p className={cn('text-[11px]', colors.textMuted)}>
          Tip: use <code className="px-1 py-0.5 rounded bg-black/5 dark:bg-white/10">&lt;p&gt;</code>,{' '}
          <code className="px-1 py-0.5 rounded bg-black/5 dark:bg-white/10">&lt;a href&gt;</code>, and{' '}
          <code className="px-1 py-0.5 rounded bg-black/5 dark:bg-white/10">&lt;table&gt;</code> for rich
          signatures. Switch to Visual to use the formatting toolbar.
        </p>
      )}
    </div>
  );
};

export default SignatureBodyEditor;
